import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import "../assets/styles/device-sessions.css";

const DEVICE_ICONS = {
  mobile: "pi pi-mobile",
  tablet: "pi pi-tablet",
};

export const formatSessionTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const REVOKED_REASONS = {
  logout: "Signed out",
  user_revoked: "Signed out by the user",
  admin_revoked: "Signed out by an admin",
  device_limit: "Signed out to free a device slot",
  replaced: "Replaced by a newer sign-in",
  password_reset: "Password was reset",
  expired: "Expired",
  account_inactive: "Account deactivated",
};

export const describeRevocation = (session) =>
  REVOKED_REASONS[session.revoked_reason] || "Ended";

export const SessionSummary = ({ session, showCurrent = false }) => (
  <div className="device-session-summary">
    <span className="device-session-icon" aria-hidden="true">
      <i className={DEVICE_ICONS[session.device_type] || "pi pi-desktop"} />
    </span>

    <div className="device-session-text">
      <strong>
        {session.device_label || "Unknown device"}
        {showCurrent && session.is_current && (
          <span className="device-session-tag">This device</span>
        )}
      </strong>
      <span>
        Last active {formatSessionTime(session.last_seen_at)}
        {session.last_ip ? ` · IP ${session.last_ip}` : ""}
      </span>
      <span>Signed in {formatSessionTime(session.created_at)}</span>
    </div>
  </div>
);

/*
 * Shown when login returns DEVICE_LIMIT_REACHED: choose which device(s) to
 * sign out, then finish logging in with the one-time challenge.
 */
export const DeviceLimitDialog = ({ visible, limitData, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) setSelected([]);
  }, [visible, limitData]);

  const toggle = (uid) =>
    setSelected((current) =>
      current.includes(uid)
        ? current.filter((value) => value !== uid)
        : [...current, uid]
    );

  const confirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(selected);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      header="Too many signed-in devices"
      visible={visible}
      onHide={() => !submitting && onCancel()}
      closable={!submitting}
      className="device-limit-dialog"
      style={{ width: "min(560px, 94vw)" }}
      modal
      footer={
        <div className="device-limit-footer">
          <button
            type="button"
            className="ghost-action"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-action"
            onClick={confirm}
            disabled={submitting || selected.length === 0}
          >
            {submitting ? "Signing in..." : "Sign out & continue"}
          </button>
        </div>
      }
    >
      <p className="device-limit-intro">
        This admin account can be signed in on up to{" "}
        <strong>{limitData?.limit}</strong> device
        {limitData?.limit === 1 ? "" : "s"} at a time. Choose a device to sign
        out and you will be signed in here.
      </p>

      {(limitData?.sessions || []).map((session) => {
        const isSelected = selected.includes(session.session_uid);

        return (
          <label
            key={session.session_uid}
            className={`device-limit-option ${isSelected ? "selected" : ""}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggle(session.session_uid)}
              aria-label={`Sign out ${session.device_label}`}
            />
            <SessionSummary session={session} />
          </label>
        );
      })}

      <p className="device-limit-intro">
        Don't recognise a device? Sign it out and change your password.
      </p>
    </Dialog>
  );
};
